/**
 * Optional client-side observability. Loads Sentry only when VITE_SENTRY_DSN
 * is set, so the bundle stays clean in development. Failures to load fall
 * back silently — telemetry is never blocking.
 */

interface MinimalSentry {
  captureException(err: unknown, ctx?: Record<string, unknown>): void;
  captureMessage(msg: string, ctx?: Record<string, unknown>): void;
}

let sentry: MinimalSentry | null = null;

export async function initObservability(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;
  try {
    // Indirect import string keeps Rollup from resolving the module at build
    // time; the dep stays optional and the bundle never grows when no DSN.
    const sentryName = "@sentry/" + "browser";
    const mod = (await import(/* @vite-ignore */ sentryName)) as {
      init: (cfg: Record<string, unknown>) => void;
    } & MinimalSentry;
    mod.init({
      dsn,
      tracesSampleRate: 0.05,
      environment: import.meta.env.MODE,
    });
    sentry = mod;
  } catch {
    // Silent — never block the app on telemetry.
  }
}

export function captureException(err: unknown, ctx?: Record<string, unknown>): void {
  try { sentry?.captureException(err, ctx); } catch { /* noop */ }
  if (typeof console !== "undefined") console.error(err);
}

export function captureMessage(msg: string, ctx?: Record<string, unknown>): void {
  try { sentry?.captureMessage(msg, ctx); } catch { /* noop */ }
}
